import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { formatPrice } from '@/lib/format';
import { useCart } from '@/lib/cartContext';
import FavoriteButton from '@/components/FavoriteButton';
import { buildImgUrls } from '@/lib/productImage';

const CARD_SIZES = '(max-width: 768px) 50vw, 25vw';

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const location = useLocation();
  // Keep the validated early-access capability when opening an unpublished shirt.
  const dropToken = location.pathname === '/forskot'
    ? new URLSearchParams(location.search).get('lykill')
    : null;
  const productUrl = `/vara/${product.id}${dropToken ? '?lykill=' + encodeURIComponent(dropToken) : ''}`;
  const [imageIndex, setImageIndex] = useState(0);
  const resetImageTimer = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const didSwipe = useRef(false);

  const images = product.images || [];
  const hasMultipleImages = images.length > 1;
  const imgLoading = index === 0 ? 'eager' : 'lazy';

  useEffect(() => {
    return () => {
      if (resetImageTimer.current) clearTimeout(resetImageTimer.current);
    };
  }, [product.id]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    didSwipe.current = false;
  };

  const handleTouchEnd = (e) => {
    if (!hasMultipleImages || touchStartX.current === null || touchStartY.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;

    // Only treat a deliberate horizontal gesture as an image swipe.
    if (Math.abs(deltaX) > 35 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25) {
      didSwipe.current = true;
      setImageIndex((current) =>
        deltaX < 0
          ? (current + 1) % images.length
          : (current - 1 + images.length) % images.length
      );

      if (resetImageTimer.current) clearTimeout(resetImageTimer.current);
      resetImageTimer.current = setTimeout(() => setImageIndex(0), 1800);
    }

    touchStartX.current = null;
    touchStartY.current = null;
  };

  const handleCardClick = (e) => {
    if (didSwipe.current) {
      e.preventDefault();
      didSwipe.current = false;
      return;
    }
    try { sessionStorage.setItem('vt-shop-scroll:' + location.pathname + location.search, String(window.scrollY)); } catch { /* Storage is optional. */ }
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const reservationExpired = product.status === 'Frátekin' && (!product.reserved_at || Date.now() - new Date(product.reserved_at).getTime() >= 10 * 60 * 1000);
    if (product.status === 'Uppseld' || (product.status === 'Frátekin' && !reservationExpired)) return;
    addItem({
      product_id: product.id,
      name: product.name,
      team: product.team,
      size: product.size,
      condition: product.condition,
      price: product.price,
      image: product.images?.[0],
      quantity: 1,
    });
  };

  const reservationExpired = product.status === 'Frátekin' && (!product.reserved_at || Date.now() - new Date(product.reserved_at).getTime() >= 10 * 60 * 1000);
  const unavailable = product.status === 'Uppseld' || (product.status === 'Frátekin' && !reservationExpired);
  const sold = product.status === 'Uppseld';

  return (
    <Link to={productUrl} state={{ from: location.pathname + location.search }} className="group flex h-full flex-col pb-3 sm:pb-4" onClick={handleCardClick}>
      <div className="relative aspect-[4/5] bg-card overflow-hidden halftone-shadow touch-pan-y" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div className="absolute inset-0 md:hidden">
          {images[imageIndex] && (() => {
            const { src: s, srcSet: ss } = buildImgUrls(images[imageIndex], 400, 5 / 4, 65);
            return <img key={images[imageIndex]} src={s} srcSet={ss} sizes={CARD_SIZES} alt={`${product.team || ''} ${product.name} treyja${product.season ? ' ' + product.season : ''}`.trim()} loading={imgLoading} decoding="async" className="absolute inset-0 w-full h-full object-cover" />;
          })()}
        </div>
        {images[0] && (() => {
          const { src: s, srcSet: ss } = buildImgUrls(images[0], 400, 5 / 4, 65);
          return <img src={s} srcSet={ss} sizes={CARD_SIZES} alt={`${product.team || ''} ${product.name} treyja${product.season ? ' ' + product.season : ''}`.trim()} loading={imgLoading} decoding="async" className="hidden md:block w-full h-full object-cover transition-opacity duration-500 md:group-hover:opacity-0" />;
        })()}
        {product.images?.length > 1 && (() => {
          const lastImg = product.images[product.images.length - 1];
          const { src: s, srcSet: ss } = buildImgUrls(lastImg, 400, 5 / 4, 65);
          return <img src={s} srcSet={ss} sizes={CARD_SIZES} alt={`${product.team || ''} ${product.name} treyja${product.season ? ' ' + product.season : ''}`.trim()} loading="lazy" decoding="async" className="hidden md:block absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500 md:group-hover:opacity-100" />;
        })()}
        {hasMultipleImages && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 md:hidden z-10" aria-hidden="true">
            {images.map((_, index) => (
              <span
                key={index}
                className={`block w-1.5 h-1.5 rounded-full shadow-sm ${index === imageIndex ? 'bg-primary-foreground' : 'bg-primary-foreground/45'}`}
              />
            ))}
          </div>
        )}
        <FavoriteButton productId={product.id} className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10" />
        {sold && (
          <div className="absolute inset-0 bg-primary/60 flex items-center justify-center">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground">Uppseld</span>
          </div>
        )}
        {!unavailable && (
          <button
            onClick={handleAddToCart}
            className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 bg-primary text-primary-foreground w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-accent"
            aria-label="Bæta í körfu"
          >
            <ShoppingBag className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="mt-2.5 sm:mt-3 flex flex-1 flex-col">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {product.season && (
            <span className="font-mono text-[9px] sm:text-[11px] uppercase tracking-[0.08em] sm:tracking-[0.1em] font-semibold text-primary bg-muted px-1.5 sm:px-2.5 py-1">{product.season}</span>
          )}
          {product.size && (
            <span className="font-mono text-[9px] sm:text-[11px] uppercase tracking-[0.08em] sm:tracking-[0.1em] font-semibold text-primary bg-muted px-1.5 sm:px-2.5 py-1">{product.size}</span>
          )}
          {product.condition && (
            <span className="font-mono text-[9px] sm:text-[11px] uppercase tracking-[0.08em] sm:tracking-[0.1em] font-semibold text-primary bg-muted px-1.5 sm:px-2.5 py-1">{product.condition}/10</span>
          )}
        </div>
        <h3 className="font-heading text-base sm:text-lg text-primary leading-tight min-h-[2.5rem] sm:min-h-[2.8rem] line-clamp-2">{product.name}</h3>
        <span className="mt-1.5 block font-mono text-sm text-primary">{formatPrice(product.price)}</span>
        <span className="mt-2.5 sm:mt-3 inline-block self-start font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.1em] sm:tracking-[0.15em] text-primary border-b border-primary pb-0.5 md:group-hover:text-accent md:group-hover:border-accent transition-colors">Skoða treyju →</span>
      </div>
    </Link>
  );
}